import { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import dayjs from 'dayjs';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useMarkIncidentUserInactive } from 'hooks/incident/useRoster';
import { useSelector } from 'react-redux';
import { useActualIncidentId } from '../IAP/hooks/commonHooks.ts';

export default function IncidentNonActiveConfirmDialog({
  show,
  onClose,
  active_time,
  user_guid,
  incident_member_id,
}) {
  const incident_id = useActualIncidentId();
  const [minutes_worked, setMinutesWorked] = useState(0);
  const [logout_time, setLogOutTime] = useState('');
  const [logoutTimeError, setLogoutTimeError] = useState({
    hasError: true,
    message: '',
    touched: false,
  });

  const group_guid = useSelector(
    (state) => state.app.currentlySelectedGroup.group_guid
  );
  const { mutate: markInactive, isLoading } = useMarkIncidentUserInactive(
    incident_id,
    group_guid
  );

  const changeDate = useCallback(
    (date) => {
      if (!active_time) {
        setLogoutTimeError({
          touched: true,
          hasError: true,
          message: 'Active time not found. Please contact administrator.',
        });
      } else {
        setLogOutTime(date);
        const endDate = dayjs(date).subtract(2, 'seconds');
        const startDate = dayjs(active_time);
        const m_worked = isNaN(endDate.diff(startDate, 'minutes'))
          ? 0
          : endDate.diff(startDate, 'minutes');
        setMinutesWorked(m_worked);
        if (endDate.isAfter(dayjs())) {
          setLogoutTimeError({
            touched: true,
            hasError: true,
            message: "Logout Time Can't be Greater Than Current Time",
          });
        } else if (m_worked < 0) {
          setLogoutTimeError({
            touched: true,
            hasError: true,
            message:
              "Invalid Date Logout Time Can't Be Earlier Than Login Time",
          });
        } else {
          setLogoutTimeError({ touched: true, hasError: false, message: '' });
        }
      }
    },
    [active_time]
  );

  useEffect(() => {
    const endDate = dayjs();
    endDate.subtract(2, 'seconds');
    //eslint-disable-next-line react-hooks/set-state-in-effect
    changeDate(endDate.format('MM/DD/YYYY HH:mm'));
  }, [active_time, changeDate]);

  // Close modal on mutation success
  useEffect(() => {
    if (!show) return;
    // No-op: modal will close on mutation success via onSuccess below
  }, [show]);

  const onTouched = () => {
    const datetime = dayjs(logout_time);
    if (datetime.isAfter(dayjs())) {
      setLogoutTimeError({
        touched: true,
        hasError: true,
        message: "Logout Time Can't be Greater Than Current Time",
      });
    } else if (minutes_worked <= 0) {
      setLogoutTimeError({
        touched: true,
        hasError: true,
        message: "Invalid Date Logout Time Can't Be Earlier Than Login Time",
      });
    } else {
      setLogoutTimeError({ touched: true, hasError: false, message: '' });
    }
  };
  const onConfirm = () => {
    if (logoutTimeError.hasError) {
      onTouched();
      return;
    }
    markInactive(
      {
        user_guid,
        logout_time: new Date(logout_time),
        minutes_worked,
        incident_member_id,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>Confirm Logout Time</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="weight-600">
          Are you sure you worked for {minutes_worked} minutes?
        </p>
        <label className="form-label d-block">Logout Time</label>
        <StylishDateTimePicker
          value={logout_time}
          onChange={changeDate}
          type={'datetime-local'}
          onBlur={onTouched}
          minDate={active_time ? new Date(active_time) : undefined}
          maxDate={new Date()}
        />
        {logoutTimeError.touched && logoutTimeError.hasError && (
          <span className="form-text form-error">
            {logoutTimeError.message}
          </span>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="button-group">
          <StylishNewButton
            className="button--secondary button--reverse"
            type="button"
            onClick={onClose}
          >
            Close
          </StylishNewButton>
          <StylishNewButton
            className="button--primary"
            type="button"
            disabled={isLoading}
            onClick={onConfirm}
          >
            Confirm
          </StylishNewButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
